import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

const FIELD_OPTIONS = [
  'requirementCode',
  'title',
  'description',
  'category',
  'ownerName',
  'testMethodFrequency',
  'lastTestDate',
  'status',
] as const;

type FieldKey = (typeof FIELD_OPTIONS)[number];

const TEMPLATE_HEADERS = [
  'requirementCode',
  'title',
  'description',
  'category',
  'ownerName',
  'testMethodFrequency',
  'lastTestDate',
  'status',
];

const STORAGE_PREFIX = 'shareweb:sedr-import-map:';

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell.trim());
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell.trim());
      if (row.some((c) => c.length > 0)) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell.trim());
    if (row.some((c) => c.length > 0)) rows.push(row);
  }

  return rows;
}

async function parseSpreadsheet(file: File): Promise<string[][]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    return parseCsv(await file.text());
  }

  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: '' }) as string[][];
  }

  throw new Error('unsupported_format');
}

function guessMapping(headers: string[]): Partial<Record<FieldKey, number>> {
  const saved = localStorage.getItem(STORAGE_PREFIX + 'default');
  if (saved) {
    try {
      return JSON.parse(saved) as Partial<Record<FieldKey, number>>;
    } catch {
      /* ignore */
    }
  }

  const mapping: Partial<Record<FieldKey, number>> = {};
  headers.forEach((header, index) => {
    const norm = header.trim().replace(/\s+/g, '').toLowerCase();
    for (const field of FIELD_OPTIONS) {
      if (norm.includes(field.toLowerCase()) || norm === field.toLowerCase()) {
        mapping[field] = index;
      }
    }
  });
  return mapping;
}

export function downloadSedrTemplate(): void {
  const csv = `${TEMPLATE_HEADERS.join(',')}\nREQ-01,Example requirement,,StaticGovernance,Owner Name,Annual review,,PendingUpload`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sedr-requirements-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function showSedrImportWizard(
  target: string | { roomId: string } | { starterSetId: string },
  onComplete: () => void,
): void {
  const roomId = typeof target === 'string'
    ? target
    : 'roomId' in target
      ? target.roomId
      : null;
  const starterSetId = typeof target === 'object' && 'starterSetId' in target
    ? target.starterSetId
    : null;

  const shell = createModalShell({
    titleKey: 'sedr.import.title',
    titleId: 'sedr-import-title',
    helpTopic: dialogHelpTopic('sedrImportWizard'),
    className: 'sedr-import-dialog',
  });

  shell.body.innerHTML = `
    <input type="file" id="sedr-import-file" accept=".csv,.xlsx,.xls" hidden />
    <div class="sedr-import-step" id="sedr-import-step-pick">
      <button type="button" class="button primary" id="sedr-import-choose" data-i18n="sedr.import.chooseFile"></button>
    </div>
    <div class="sedr-import-step" id="sedr-import-step-map" hidden></div>
    <div class="sedr-import-step" id="sedr-import-step-preview" hidden></div>
    <p class="muted" id="sedr-import-success" hidden></p>
    <p class="error-text" id="sedr-import-error" hidden></p>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button subtle';
  cancelButton.id = 'sedr-import-close';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const commitButton = document.createElement('button');
  commitButton.type = 'button';
  commitButton.className = 'button primary';
  commitButton.id = 'sedr-import-commit';
  commitButton.hidden = true;
  commitButton.setAttribute('data-i18n', 'sedr.import.commit');

  shell.actions.append(cancelButton, commitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  let parsedRows: string[][] = [];
  let headers: string[] = [];
  let mapping: Partial<Record<FieldKey, number>> = {};

  const close = () => {
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  const errorEl = shell.body.querySelector('#sedr-import-error') as HTMLParagraphElement;
  const successEl = shell.body.querySelector('#sedr-import-success') as HTMLParagraphElement;
  const showError = (msg: string) => {
    successEl.hidden = true;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };
  const showSuccess = (msg: string) => {
    errorEl.hidden = true;
    successEl.textContent = msg;
    successEl.hidden = false;
  };

  const fileInput = shell.body.querySelector('#sedr-import-file') as HTMLInputElement;
  shell.body.querySelector('#sedr-import-choose')?.addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    errorEl.hidden = true;
    successEl.hidden = true;
    try {
      parsedRows = await parseSpreadsheet(file);
      if (parsedRows.length < 2) throw new Error('empty_file');
      headers = parsedRows[0];
      mapping = guessMapping(headers);
      renderMappingStep();
    } catch {
      showError(t('sedr.import.parseError'));
    }
  });

  function renderMappingStep() {
    const mapEl = shell.body.querySelector('#sedr-import-step-map') as HTMLElement;
    mapEl.hidden = false;
    mapEl.innerHTML = `<h3 data-i18n="sedr.import.mapping"></h3>`;
    applyI18n(mapEl);

    const table = document.createElement('table');
    table.className = 'data-table sedr-import-map-table';
    for (const field of FIELD_OPTIONS) {
      const row = document.createElement('tr');
      const label = document.createElement('td');
      label.textContent = field;
      const selectCell = document.createElement('td');
      const select = document.createElement('select');
      select.dataset.field = field;
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = '—';
      select.append(empty);
      headers.forEach((header, index) => {
        const opt = document.createElement('option');
        opt.value = String(index);
        opt.textContent = header || `Column ${index + 1}`;
        if (mapping[field] === index) opt.selected = true;
        select.append(opt);
      });
      select.addEventListener('change', () => {
        const val = select.value;
        if (val === '') delete mapping[field];
        else mapping[field as FieldKey] = Number(val);
      });
      selectCell.append(select);
      row.append(label, selectCell);
      table.append(row);
    }

    mapEl.append(table);
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'button primary';
    nextBtn.textContent = t('sedr.import.preview');
    nextBtn.addEventListener('click', renderPreviewStep);
    mapEl.append(nextBtn);
  }

  function buildImportRows(): Record<string, unknown>[] {
    const codeIdx = mapping.requirementCode;
    const titleIdx = mapping.title;
    if (codeIdx === undefined || titleIdx === undefined) return [];

    return parsedRows.slice(1).map((row) => {
      const get = (field: FieldKey) => {
        const idx = mapping[field];
        return idx === undefined ? undefined : row[idx]?.trim();
      };
      const lastTest = get('lastTestDate');
      return {
        requirementCode: get('requirementCode') ?? '',
        title: get('title') ?? '',
        description: get('description') || null,
        category: get('category'),
        ownerName: get('ownerName'),
        testMethodFrequency: get('testMethodFrequency'),
        lastTestDate: lastTest ? new Date(lastTest).toISOString() : null,
        status: get('status'),
      };
    });
  }

  function renderPreviewStep() {
    const previewEl = shell.body.querySelector('#sedr-import-step-preview') as HTMLElement;
    const commitBtn = commitButton;
    previewEl.hidden = false;
    commitBtn.hidden = false;

    const rows = buildImportRows().filter((r) => r.requirementCode && r.title);
    previewEl.replaceChildren();
    const count = document.createElement('p');
    count.textContent = t('sedr.import.previewCount', { count: rows.length });
    previewEl.append(count);

    const table = document.createElement('table');
    table.className = 'data-table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const codeTh = document.createElement('th');
    codeTh.setAttribute('data-i18n', 'sedr.table.reqId');
    const titleTh = document.createElement('th');
    titleTh.setAttribute('data-i18n', 'sedr.table.title');
    headRow.append(codeTh, titleTh);
    thead.append(headRow);
    table.append(thead);

    const tbody = document.createElement('tbody');
    rows.slice(0, 10).forEach((row) => {
      const tr = document.createElement('tr');
      const codeTd = document.createElement('td');
      codeTd.textContent = String(row.requirementCode ?? '');
      const titleTd = document.createElement('td');
      titleTd.textContent = String(row.title ?? '');
      tr.append(codeTd, titleTd);
      tbody.append(tr);
    });
    table.append(tbody);
    previewEl.append(table);
    applyI18n(previewEl);

    localStorage.setItem(STORAGE_PREFIX + 'default', JSON.stringify(mapping));

    commitBtn.onclick = async () => {
      commitBtn.disabled = true;
      try {
        const result = starterSetId
          ? await sedrApi.importStarterSetItems(starterSetId, rows)
          : await sedrApi.importRequirements(roomId!, rows);
        if (result.created === 0) {
          showError(t('sedr.import.allSkipped', { skipped: result.skipped }));
          commitBtn.hidden = true;
          return;
        }
        showSuccess(t('sedr.import.done', { created: result.created, skipped: result.skipped }));
        commitBtn.hidden = true;
        window.setTimeout(() => {
          close();
          onComplete();
        }, 900);
      } catch (cause) {
        showError(t(sedrErrorMessageKey(cause)));
        commitBtn.disabled = false;
      }
    };
  }
}
